import React, { useState, useEffect, useCallback } from 'react';
import { useKanban } from './KanbanContext';
import { useAuth } from './AuthContext';
import { fetchTasksFromLambda, updateTaskInLambda } from '../utils/api/tasksApi';
import { downloadFileFromS3 } from '../utils/api/s3Api';
import useEscapeKey from '../utils/useEscapeKey';
import { generateLocalTimestamp } from '../utils/taskUtils';
import { 
  getActivityIcon,
  getActivityDescription,
  formatDate,
  formatUserName,
  parseTimestamp,
  ACTIVITY_TYPES,
} from '../utils/activityHelpers';

function ActivityHistory({ isOpen, onClose }) {
  const { activeWorkspace, reloadTasksFromServer } = useKanban();
  const { currentUser } = useAuth();
  const [historyTasks, setHistoryTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [restoringTaskId, setRestoringTaskId] = useState(null);
  const [restoreMessage, setRestoreMessage] = useState(null);

  // Cerrar modal con tecla Escape
  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  }, isOpen);

  // La función formatUserName ahora se importa desde activityHelpers

  // La función formatDate ahora se importa desde activityHelpers

  // Las funciones de parsing de timestamps ahora se importan desde activityHelpers

  const loadHistoryTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Obtener todas las tareas del workspace (incluyendo eliminadas y archivadas)
      const allTasks = await fetchTasksFromLambda(activeWorkspace, true);
      
      // Filtrar solo las tareas eliminadas o archivadas
      const filteredTasks = allTasks.filter(task => 
        task.status === 'deleted' || task.ArchivedDate === true
      );
      
      // Ordenar por fecha de creación (más reciente primero)
      const sortedTasks = filteredTasks.sort((a, b) => {
        const dateA = parseTimestamp(a.createdTimestamp);
        const dateB = parseTimestamp(b.createdTimestamp);
        
        // Usar .getTime() para comparación consistente y agregar logging para debug
        const timeA = dateA.getTime();
        const timeB = dateB.getTime();
        
        // Si alguna fecha es inválida (timestamp = 0), colocarla al final
        if (timeA === 0 && timeB === 0) return 0;
        if (timeA === 0) return 1;
        if (timeB === 0) return -1;
        
        return timeB - timeA; // Más reciente primero
      });
      
      setHistoryTasks(sortedTasks);
      setFilteredTasks(sortedTasks); // Inicialmente mostrar todas las tareas
    } catch (err) {
      setError('Error al cargar el historial de tareas');
    } finally {
      setLoading(false);
    }
  }, [activeWorkspace]);

  // Filtrar tareas basado en la búsqueda
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTasks(historyTasks);
    } else {
      const filtered = historyTasks.filter(task => 
        task.title && task.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTasks(filtered);
    }
  }, [searchQuery, historyTasks]);

  // Manejar cambios en la búsqueda
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Limpiar búsqueda
  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleRestoreTask = async (task) => {
    const taskId = task.id || task.uuid;
    if (!taskId || restoringTaskId) return;

    const title = task.title || 'Sin título';
    const confirmed = window.confirm(
      `¿Restaurar la tarea "${title}" a En progreso?\n\nSolo se restaurará esta tarea.`
    );
    if (!confirmed) return;

    setRestoringTaskId(taskId);
    setRestoreMessage(null);
    setError(null);

    try {
      const timestamp = generateLocalTimestamp();
      const userEmail = currentUser?.email || 'Usuario';
      const previousStatus = task.status === 'deleted' ? 'deleted' : (task.ArchivedDate ? 'archived' : task.status);

      const restoreActivity = {
        timestamp,
        type: ACTIVITY_TYPES.TASK_RESTORED,
        field: 'status',
        oldValue: previousStatus,
        newValue: 'inProgress',
        updatedBy: userEmail,
      };

      const updates = {
        status: 'inProgress',
        ArchivedDate: false,
        completedDate: '',
        activityHistory: [...(task.activityHistory || []), restoreActivity],
        updatedAt: timestamp,
      };

      // Si no tiene responsable, asignar al usuario actual (igual que al mover a inProgress)
      if (!task.assignee && currentUser?.email) {
        updates.assignee = currentUser.email;
        updates.activityHistory = [
          ...updates.activityHistory,
          {
            timestamp,
            type: ACTIVITY_TYPES.ASSIGNEE_CHANGE,
            field: 'assignee',
            oldValue: null,
            newValue: currentUser.email,
            updatedBy: userEmail,
          },
        ];
      }

      await updateTaskInLambda(taskId, updates);
      await reloadTasksFromServer(activeWorkspace);

      setHistoryTasks((prev) => prev.filter((t) => (t.id || t.uuid) !== taskId));
      setRestoreMessage(`Tarea "${title}" restaurada a En progreso.`);
    } catch (err) {
      console.error('Error al restaurar tarea:', err);
      setError(err.message || 'Error al restaurar la tarea');
    } finally {
      setRestoringTaskId(null);
    }
  };

  // Cargar tareas del historial cuando se abre el modal
  useEffect(() => {
    if (isOpen && activeWorkspace) {
      setRestoreMessage(null);
      loadHistoryTasks();
    }
  }, [isOpen, activeWorkspace, loadHistoryTasks]);

  if (!isOpen) return null;

  return (
    <div className="activity-history-overlay" onClick={onClose}>
      <div className="activity-history-modal" onClick={(e) => e.stopPropagation()}>
        <div className="activity-history-header">
          <h2>📋 Historial de tareas</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="activity-history-content">
          {loading && (
            <div className="loading-message">
              <p>Cargando historial...</p>
            </div>
          )}
          
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {restoreMessage && (
            <div className="restore-success-message">
              <p>{restoreMessage}</p>
            </div>
          )}
          
          {!loading && !error && historyTasks.length === 0 && (
            <div className="empty-message">
              <p>No hay tareas eliminadas o archivadas en este workspace.</p>
            </div>
          )}
          
          {!loading && !error && historyTasks.length > 0 && (
            <div className="history-tasks-list">
              {/* Buscador */}
              <div className="search-container">
                <div className="search-input-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Buscar tareas por título..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  {searchQuery && (
                    <button 
                      className="clear-search-btn"
                      onClick={clearSearch}
                      title="Limpiar búsqueda"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              
              <div className="history-stats">
                <p>
                  {searchQuery ? (
                    <>
                      <strong>{filteredTasks.length}</strong> de <strong>{historyTasks.length}</strong> tarea{historyTasks.length !== 1 ? 's' : ''} 
                      {searchQuery && ` que coinciden con "${searchQuery}"`}
                    </>
                  ) : (
                    <>
                      <strong>{historyTasks.length}</strong> tarea{historyTasks.length !== 1 ? 's' : ''} 
                      {' '}eliminada{historyTasks.length !== 1 ? 's' : ''} o archivada{historyTasks.length !== 1 ? 's' : ''}
                    </>
                  )}
                </p>
              </div>
              
              {filteredTasks.length === 0 && searchQuery && (
                <div className="no-results-message">
                  <p>No se encontraron tareas que coincidan con "{searchQuery}"</p>
                </div>
              )}
              
              <div className="history-tasks-grid">
                {filteredTasks.map((task) => (
                  <div key={task.id || task.uuid} className="history-task-item">
                    <div className="history-task-header">
                      <div className="history-task-status">
                        {task.status === 'deleted' && (
                          <span className="status-badge deleted">🗑️ Eliminada</span>
                        )}
                        {task.ArchivedDate === true && (
                          <span className="status-badge archived">📦 Archivada</span>
                        )}
                      </div>
                      <div className="history-task-date">
                        {formatDate(task.createdTimestamp)}
                      </div>
                    </div>
                    
                    <div className="history-task-details">
                      <h4 className="history-task-title">{task.title || 'Sin título'}</h4>
                      
                      {task.description && (
                        <p className="history-task-description">
                          {task.description.length > 200 
                            ? task.description.substring(0, 200) + '...'
                            : task.description
                          }
                        </p>
                      )}
                      
                      <div className="history-task-meta">
                        <div className="history-task-meta-row">
                          {task.createdBy && (
                            <span className="history-task-creator">
                              👤 Creado por: {formatUserName(task.createdBy)}
                            </span>
                          )}
                          
                          {task.assignee && (
                            <span className="history-task-assignee">
                              📋 Responsable: {formatUserName(task.assignee)}
                            </span>
                          )}
                        </div>
                        
                        {task.endDate && (
                          <div className="history-task-meta-row">
                            <span className="history-task-deadline">
                              📅 Vencimiento: {task.endDate}
                            </span>
                          </div>
                        )}
                        
                        {task.tags && task.tags.length > 0 && (
                          <div className="history-task-meta-row">
                            <div className="history-task-tags">
                              <span className="tags-label">🏷️ Etiquetas:</span>
                              {task.tags.map((tag, index) => (
                                <span key={index} className="history-tag">{tag}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {((task.files && task.files.length > 0) || (task.url && task.url.length > 0)) && (
                          <div className="history-task-meta-row">
                            <div className="history-task-attachments">
                              {task.files && task.files.length > 0 && (
                                <div className="history-task-files">
                                  <span className="files-label">📎 Archivos:</span>
                                  <div className="files-list">
                                    {task.files.map((file, index) => (
                                      <button
                                        key={index}
                                        className="file-link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          downloadFileFromS3(file);
                                        }}
                                        title={`Descargar ${file}`}
                                      >
                                        📄 {file}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {task.url && task.url.length > 0 && (
                                <div className="history-task-urls">
                                  <span className="urls-label">🔗 Enlaces:</span>
                                  <div className="urls-list">
                                    {(Array.isArray(task.url) ? task.url : [task.url]).map((url, index) => (
                                      <a
                                        key={index}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="url-link"
                                        onClick={(e) => e.stopPropagation()}
                                        title={`Abrir ${url}`}
                                      >
                                        🔗 {url.length > 50 ? url.substring(0, 47) + '...' : url}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {task.comments && task.comments.length > 0 && (
                          <div className="history-task-meta-row">
                            <div className="history-task-comments">
                              <span className="comments-label">💬 Comentarios:</span>
                              <div className="comments-list">
                                {task.comments.map((comment, index) => {
                                  const commentText = (() => {
                                    if (typeof comment.text === 'object' && comment.text !== null && typeof comment.text.text === 'string') {
                                      return comment.text.text;
                                    } else if (typeof comment.text === 'string') {
                                      return comment.text;
                                    } else {
                                      return JSON.stringify(comment.text);
                                    }
                                  })();
                                  
                                  return (
                                    <div key={comment.id || `comment-${index}`} className="history-comment">
                                      <div className="comment-header">
                                        <span className="comment-author">
                                          {comment.isEvidence ? '📋' : '👤'} {formatUserName(comment.author || 'Usuario')}
                                          {comment.isEvidence && <span className="evidence-badge">Evidencia</span>}
                                        </span>
                                        <span className="comment-date">
                                          {formatDate(comment.timestamp)}
                                        </span>
                                      </div>
                                      <div className="comment-text">
                                        {commentText}
                                      </div>
                                      {comment.attachments && comment.attachments.length > 0 && (
                                        <div className="comment-attachments">
                                          <span className="attachments-label">📎 Archivos adjuntos:</span>
                                          <div className="attachments-list">
                                            {comment.attachments.map((attachment, attachIndex) => (
                                              <a
                                                key={`${comment.id || index}-attachment-${attachIndex}`}
                                                href={attachment.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="attachment-link"
                                                onClick={(e) => e.stopPropagation()}
                                                title={`Descargar ${attachment.name}`}
                                              >
                                                📄 {attachment.name}
                                              </a>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                          )}
                        
                        {/* Historial de actividades de la tarea */}
                        {task.activityHistory && task.activityHistory.length > 0 && (
                          <div className="history-task-meta-row">
                            <div className="history-task-activities">
                              <span className="activities-label">📋 Historial de actividades:</span>
                              <div className="activities-list">
                                {task.activityHistory
                                  .sort((a, b) => {
                                    const dateA = parseTimestamp(a.timestamp);
                                    const dateB = parseTimestamp(b.timestamp);
                                    const timeA = dateA.getTime();
                                    const timeB = dateB.getTime();
                                    
                                    // Si los timestamps son diferentes, usar orden cronológico normal (más reciente primero)
                                    if (timeA !== timeB) {
                                      return timeB - timeA;
                                    }
                                    
                                    // Si los timestamps son iguales, priorizar la creación al final (primera cronológicamente)
                                    if (a.type === 'CREATION' && b.type !== 'CREATION') {
                                      return 1; // a va después (más abajo en la lista visual)
                                    }
                                    if (b.type === 'CREATION' && a.type !== 'CREATION') {
                                      return -1; // b va después (a va arriba)
                                    }
                                    
                                    return 0;
                                  })
                                  .map((activity, actIndex) => {
                                    // Usar la función centralizada para obtener iconos
                                    
                                    // Usar la función centralizada para obtener descripciones
                                    
                                    return (
                                      <div key={`activity-${actIndex}`} className="history-activity-item">
                                        <span className="activity-icon">{getActivityIcon(activity.type)}</span>
                                        <div className="activity-details">
                                          <div className="activity-description">
                                            {getActivityDescription(activity.type, activity)}
                                          </div>
                                          <div className="activity-timestamp">
                                            {formatDate(activity.timestamp)}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {task.completedDate && (
                        <div className="history-task-completed">
                          ✅ Completada: {formatDate(task.completedDate)}
                        </div>
                      )}

                      <div className="history-task-actions">
                        <button
                          type="button"
                          className="restore-task-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestoreTask(task);
                          }}
                          disabled={restoringTaskId === (task.id || task.uuid)}
                          title="Restaurar esta tarea a En progreso"
                        >
                          {restoringTaskId === (task.id || task.uuid)
                            ? 'Restaurando...'
                            : '♻️ Restaurar a En progreso'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActivityHistory; 